import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { JobFields } from "@/components/JobFields";
import { emptyJobFields } from "@/lib/jobFields";
import {
  CreditCard, Loader2, CheckCircle2, Star, Wallet, FileWarning, TrendingUp, Briefcase,
  Mail, Phone, ShieldCheck, LifeBuoy, Building2, Plus, Receipt, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const INV_COLOR = { paid: "#10B981", open: "#F5A623", partially_paid: "#002EA6", overdue: "#EF4444", void: "#71717A" };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const daysSince = (iso) => (Date.now() - new Date(iso).getTime()) / 86400000;

const typeLabel = (i) => {
  if (i.invoice_type === "job_posting") return i.featured ? "Featured Upgrade" : "Job Posting";
  if (i.invoice_type === "engaged_search" || i.invoice_type === "retained_search") return "Search Invoice";
  if (i.invoice_type === "placement_fee") return "Placement Fee";
  if (i.invoice_type === "subscription") return "Subscription";
  return (i.invoice_type || "invoice").replace(/_/g, " ");
};

// effective status incl. overdue derivation
const effStatus = (i) => {
  if (i.status === "paid" || i.status === "void") return i.status;
  if ((i.status === "open" || i.status === "partially_paid") && daysSince(i.created_at) > 30) return "overdue";
  return i.status;
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "engaged_search", label: "Search Invoices" },
  { id: "job_posting", label: "Job Postings" },
  { id: "featured", label: "Featured Upgrades" },
  { id: "placement_fee", label: "Placement Fees" },
  { id: "paid", label: "Paid" },
  { id: "due", label: "Due" },
  { id: "overdue", label: "Overdue" },
];

function Card({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-white border border-black/10 p-5" data-testid={`bill-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="overline text-[0.55rem]">{label}</p>
        <Icon className="h-4 w-4" style={{ color: tone }} strokeWidth={1.6} />
      </div>
      <p className="font-heading font-black text-2xl mt-3">{value}</p>
    </div>
  );
}

export default function ClientBilling() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState(null);
  const [org, setOrg] = useState(null);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [paying, setPaying] = useState(false);

  // post a job
  const [packages, setPackages] = useState([]);
  const [jobOpen, setJobOpen] = useState(false);
  const [jf, setJf] = useState({ title: "", description: "", ...emptyJobFields });
  const [jpdf, setJpdf] = useState(null);
  const [pkg, setPkg] = useState("standard");
  const [posting, setPosting] = useState(false);

  const load = () => api.get("/invoices").then(({ data }) => setInvoices(data));
  useEffect(() => {
    load();
    api.get("/billing/config").then(({ data }) => setPackages(data.job_posting_packages)).catch(() => {});
    api.get("/client/organization").then(({ data }) => setOrg(data)).catch(() => {});
  }, []);

  const stats = useMemo(() => {
    const list = invoices || [];
    const outstanding = list.filter((i) => i.status !== "paid" && i.status !== "void")
      .reduce((s, i) => s + Math.max(0, i.amount - (i.amount_paid || 0)), 0);
    const open = list.filter((i) => i.status === "open" || i.status === "partially_paid").length;
    const yr = new Date().getFullYear();
    const paidYtd = list.filter((i) => new Date(i.created_at).getFullYear() === yr)
      .reduce((s, i) => s + (i.amount_paid || 0), 0);
    const postingCharges = list.filter((i) => i.invoice_type === "job_posting")
      .reduce((s, i) => s + (i.amount || 0), 0);
    return { outstanding, open, paidYtd, postingCharges };
  }, [invoices]);

  const filtered = useMemo(() => {
    const list = invoices || [];
    return list.filter((i) => {
      const es = effStatus(i);
      switch (filter) {
        case "all": return true;
        case "engaged_search": return i.invoice_type === "engaged_search" || i.invoice_type === "retained_search";
        case "job_posting": return i.invoice_type === "job_posting" && !i.featured;
        case "featured": return i.invoice_type === "job_posting" && i.featured;
        case "placement_fee": return i.invoice_type === "placement_fee";
        case "paid": return es === "paid";
        case "due": return es === "open" || es === "partially_paid";
        case "overdue": return es === "overdue";
        default: return true;
      }
    });
  }, [invoices, filter]);

  const history = useMemo(() => (invoices || []).filter((i) => (i.amount_paid || 0) > 0)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)), [invoices]);

  const payInvoice = async (invoiceId, amount) => {
    setPaying(true);
    try {
      const { data } = await api.post("/billing/checkout", { kind: "invoice_payment", invoice_id: invoiceId, amount, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPaying(false); }
  };

  const postJob = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const { data: job } = await api.post("/jobs", { ...jf });
      if (jpdf) {
        const fd = new FormData(); fd.append("file", jpdf);
        await api.post(`/jobs/${job.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const { data: co } = await api.post("/billing/checkout", { kind: "job_posting", package_id: pkg, job_id: job.id, origin_url: window.location.origin });
      window.location.href = co.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPosting(false); }
  };

  const remainingOf = (i) => Math.round((i.amount - (i.amount_paid || 0)) * 100) / 100;

  return (
    <div>
      <PageHeader overline="Manage invoices, payments, contracts, and posting charges." title="Billing">
        <Dialog open={jobOpen} onOpenChange={setJobOpen}>
          <DialogTrigger asChild>
            <Button data-testid="post-job-btn" className="rounded-none bg-primary hover:bg-[#09090B] transition-colors"><Plus className="h-4 w-4 mr-1" /> Post a job</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-black/10 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Post a single job</DialogTitle></DialogHeader>
            <form onSubmit={postJob} className="space-y-4">
              <div className="space-y-2">
                <p className="overline text-[0.55rem]">Package</p>
                <div className="grid grid-cols-2 gap-px bg-black/10 border border-black/10">
                  {packages.map((p) => {
                    const on = pkg === p.id;
                    return (
                      <button type="button" key={p.id} data-testid={`pkg-${p.id}`} onClick={() => setPkg(p.id)}
                        className={`p-4 text-left transition-colors ${on ? "bg-[#09090B] text-white" : "bg-[#F4F4F5] hover:bg-black/5"}`}>
                        <div className="flex items-baseline justify-between">
                          <span className="font-heading font-bold text-sm flex items-center gap-1">{p.name.replace(" Posting", "")}{p.id === "featured" && <Star className="h-3 w-3 text-primary fill-primary" />}</span>
                          <span className="font-heading font-black text-xl">${p.price}</span>
                        </div>
                        <span className={`text-[0.6rem] ${on ? "text-white/60" : "text-muted-foreground"}`}>{p.features[0]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <JobFields values={jf} setValues={setJf} pdf={jpdf} setPdf={setJpdf} />
              <DialogFooter>
                <Button type="submit" data-testid="post-job-submit" disabled={posting} className="rounded-none bg-primary hover:bg-[#09090B]">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Continue to checkout · $${packages.find((p) => p.id === pkg)?.price ?? ""}`}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-8 space-y-8">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card icon={Wallet} label="Outstanding Balance" value={money(stats.outstanding)} tone="#F5A623" />
          <Card icon={FileWarning} label="Open Invoices" value={stats.open} tone="#002EA6" />
          <Card icon={TrendingUp} label="Paid YTD" value={money(stats.paidYtd)} tone="#10B981" />
          <Card icon={Briefcase} label="Job Posting Charges" value={money(stats.postingCharges)} tone="#8b5cf6" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)}
              className={`px-4 py-2 text-xs font-medium border transition-colors ${filter === f.id ? "bg-[#09090B] text-white border-[#09090B]" : "bg-white border-black/10 hover:bg-black/5"}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Invoice table */}
        <div>
          {!invoices && <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
          {invoices && filtered.length === 0 && <Empty icon={CreditCard} title="No invoices here" hint="Invoices appear after you post a job or begin an engaged search." />}
          {invoices && filtered.length > 0 && (
            <div className="border border-black/10" data-testid="invoice-table">
              <div className="grid grid-cols-12 px-5 py-3 border-b border-black/10 overline text-[0.55rem] bg-white">
                <span className="col-span-4">Type</span><span className="col-span-2">Date</span><span className="col-span-2">Amount</span><span className="col-span-2">Status</span><span className="col-span-2 text-right">Action</span>
              </div>
              {filtered.map((i) => {
                const es = effStatus(i);
                const remaining = remainingOf(i);
                return (
                  <button key={i.id} onClick={() => setActive(i)} data-testid={`invoice-row-${i.id}`}
                    className="w-full grid grid-cols-12 px-5 py-4 border-b border-black/5 bg-white items-center text-left hover:bg-[#F1F1F3] transition-colors">
                    <span className="col-span-4 text-sm flex flex-col">
                      <span className="flex items-center gap-2">
                        {i.invoice_type === "job_posting" && i.featured && <Star className="h-3.5 w-3.5 text-primary fill-primary" />}
                        {typeLabel(i)}
                        {i.allow_installments && <span className="text-[0.5rem] font-mono text-primary border border-primary/30 px-1">{i.installments}× PLAN</span>}
                      </span>
                      {i.description && <span className="text-[0.7rem] text-muted-foreground truncate max-w-[95%] mt-0.5">{i.description}</span>}
                    </span>
                    <span className="col-span-2 text-xs text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</span>
                    <span className="col-span-2 font-heading font-bold text-sm">{money(i.amount)}</span>
                    <span className="col-span-2 flex items-center gap-1.5 text-xs capitalize"><span className="h-1.5 w-1.5 rounded-full" style={{ background: INV_COLOR[es] || "#71717A" }} />{es.replace(/_/g, " ")}</span>
                    <span className="col-span-2 text-right text-xs text-primary flex items-center justify-end gap-1">{es !== "paid" ? <>Pay <ArrowRight className="h-3 w-3" /></> : <span className="text-muted-foreground">View</span>}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Secondary panels */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Payment history */}
          <div className="lg:col-span-2 border border-black/10 bg-white p-6">
            <p className="overline mb-4 flex items-center gap-2"><Receipt className="h-3.5 w-3.5" /> Payment History</p>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No payments recorded yet.</p>
            ) : (
              <div className="space-y-px bg-black/10 border border-black/10">
                {history.map((i) => (
                  <div key={i.id} className="bg-white px-4 py-3 flex items-center justify-between" data-testid={`payment-${i.id}`}>
                    <div>
                      <p className="text-sm font-medium">{typeLabel(i)}</p>
                      <p className="text-[0.6rem] text-muted-foreground">{new Date(i.updated_at).toLocaleDateString()}</p>
                    </div>
                    <span className="font-heading font-bold text-sm text-[#10B981]">{money(i.amount_paid)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-6">
            <div className="border border-black/10 bg-white p-6" data-testid="billing-contact">
              <p className="overline mb-4 flex items-center gap-2"><Building2 className="h-3.5 w-3.5" /> Billing Contact</p>
              <p className="font-medium text-sm">{user?.full_name || "—"}</p>
              {org?.name && <p className="text-sm text-muted-foreground">{org.name}</p>}
              <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {user?.email}</p>
            </div>

            <div className="border border-black/10 bg-white p-6">
              <p className="overline mb-4 flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Payment Method</p>
              <p className="text-sm">Secured by <b>Stripe</b></p>
              <p className="text-sm text-muted-foreground mt-1">Cards are entered at checkout — no card details are stored on file.</p>
            </div>

            <div className="border border-black/10 bg-[#09090B] text-white p-6">
              <p className="overline mb-3 flex items-center gap-2 text-white/60"><LifeBuoy className="h-3.5 w-3.5" /> Need help?</p>
              <p className="text-sm text-white/80">Questions about an invoice, a placement fee or a custom search engagement?</p>
              <a href="mailto:billing@innovationtalentpartners.com" data-testid="contact-team-btn" className="mt-4 inline-flex items-center gap-2 text-sm font-medium bg-white text-[#09090B] px-4 py-2.5 hover:bg-white/90 transition-colors">
                <Mail className="h-4 w-4" /> Contact our team
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice detail */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-white border-black/10 rounded-none max-w-lg" data-testid="invoice-detail">
          {active && (() => {
            const es = effStatus(active);
            const remaining = remainingOf(active);
            const perInstallment = Math.min(remaining, Math.round((active.amount / (active.installments || 1)) * 100) / 100);
            return (
              <>
                <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">
                  {active.invoice_type === "job_posting" && active.featured && <Star className="h-4 w-4 text-primary fill-primary" />}
                  {typeLabel(active)}
                </DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="overline text-[0.55rem]">Amount</p><p className="font-heading font-black text-2xl mt-0.5">{money(active.amount)}</p></div>
                    <div><p className="overline text-[0.55rem]">Status</p><p className="text-sm mt-1 flex items-center gap-1.5 capitalize"><span className="h-1.5 w-1.5 rounded-full" style={{ background: INV_COLOR[es] }} />{es.replace(/_/g, " ")}</p></div>
                    <div><p className="overline text-[0.55rem]">Issued</p><p className="text-sm mt-0.5">{new Date(active.created_at).toLocaleDateString()}</p></div>
                    <div><p className="overline text-[0.55rem]">Reference</p><p className="text-sm mt-0.5 font-mono text-xs">{active.stripe_invoice_id ? active.stripe_invoice_id.slice(-10) : active.id.slice(-10)}</p></div>
                  </div>
                  {active.description && (
                    <div className="border-l-2 border-primary/40 pl-3">
                      <p className="overline text-[0.55rem] mb-1">What this is for</p>
                      <p className="text-sm text-[#27272A] whitespace-pre-wrap">{active.description}</p>
                    </div>
                  )}
                  {(active.amount_paid || 0) > 0 && active.status !== "paid" && (
                    <div className="border border-black/10 bg-[#F4F4F5] p-3 text-sm">{money(active.amount_paid)} paid · <b>{money(remaining)} remaining</b></div>
                  )}
                  {active.allow_installments && active.status !== "paid" && (
                    <p className="text-xs text-muted-foreground">Installment plan available: {active.installments} payments of {money(perInstallment)}.</p>
                  )}
                  {es !== "paid" ? (
                    <div className="flex items-center gap-3 pt-2">
                      {active.allow_installments && (
                        <Button onClick={() => payInvoice(active.id, perInstallment)} disabled={paying} data-testid="pay-installment-btn" variant="ghost" className="rounded-none border border-black/10 flex-1">Pay installment · {money(perInstallment)}</Button>
                      )}
                      <Button onClick={() => payInvoice(active.id, undefined)} disabled={paying} data-testid="pay-now-btn" className="rounded-none bg-primary hover:bg-[#09090B] flex-1">
                        {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Pay now · {money(remaining)}</>}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-[#10B981] text-sm font-medium pt-2"><CheckCircle2 className="h-5 w-5" /> Paid in full</div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
